import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useInRouterContext, useLocation } from 'react-router'
import type { AuthTokens } from '@/types'
import {
  api,
  login as apiLogin,
  logout as apiLogout,
  setTokens,
  clearTokens,
  hasRefreshToken,
  refreshTokensSingleFlight,
} from '@/lib/api'
import { disableAnalyticsForPage, identifyUser, resetIdentity, syncSessionReplay } from '@/lib/analytics'
import {
  getSupportSessionStatus,
  isSupportSession,
  setSupportSessionInfo,
  type SupportSessionInfo,
} from '@/lib/supportSession'
import { clearSentrySession, setSentryUser, tagSentrySession } from '@/lib/sentryUtils'
import { registerOperatorPush, unregisterOperatorPush } from '@/lib/push/operatorPush'
import { AuthContext } from './authContextDef'
import { useSelectedPropertyStore } from '@/stores/useSelectedPropertyStore'
import { useSearchStore } from '@/stores/useSearchStore'
import type { User } from '@/types'

const CLIENT_ID = import.meta.env.VITE_CLIENT_ID || ''

// EP-160 section 2.3: GET /api/session carries `support_session` while a
// support session token is the bearer.
type SessionUser = User & { support_session?: SupportSessionInfo | null }

// EP-160 D7: tells analytics.ts the route on every navigation, which starts or
// stops the masked replay. Rendered only inside a router.
function SessionReplayRoute() {
  const { pathname } = useLocation()
  useEffect(() => {
    syncSessionReplay(pathname)
  }, [pathname])
  return null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const inRouter = useInRouterContext()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch current user. Returns the fetched user (or null) so callers that run
  // in the same tick as login() can act on the role without waiting for the
  // `user` state to flush (e.g. LoginPage's owner-only onboarding redirect).
  const fetchUser = async (): Promise<User | null> => {
    try {
      const response = await api.get<{ data: SessionUser }>('/api/session')
      const fetchedUser = response.data.data
      const supportSession = fetchedUser.support_session ?? null
      setUser(fetchedUser)
      // D3: scope the persisted property pick to this account. Covers every
      // successful session fetch, including a token-expiry-then-relogin as a
      // different account with no explicit Logout in between (the existing
      // WEB-H5 clearing in logout() only covers the explicit-Logout path).
      useSelectedPropertyStore.getState().syncAccount(fetchedUser.account.id)
      // EP-160: Sentry tags (account id, kind, role) do not wait for the hash.
      const { account } = fetchedUser
      tagSentrySession({
        accountId: account.id,
        accountKind: account.kind,
        role: fetchedUser.role,
        supportSessionId: supportSession?.id,
      })
      // EP-160 section 2.3: FlowRent support working in the owner's account.
      // No PostHog identity, no Sentry user, no client capture for the rest
      // of this page; the owner's own sign-ins are what analytics count.
      if (supportSession || isSupportSession()) {
        disableAnalyticsForPage()
        if (supportSession) setSupportSessionInfo(supportSession)
        return fetchedUser
      }
      // C1 — identify the user in PostHog with a hashed id (no PII).
      // Uses SHA-256(account_id:user_id) which matches the Rails server-side hash
      // so client + server events stitch to the same PostHog person profile.
      // EP-160: the same hash is the Sentry user.
      identifyUser(account.id, fetchedUser.id, account.kind)
        .then(setSentryUser)
        .catch(() => {
          // Non-fatal: if crypto.subtle is unavailable (old browser), skip silently.
        })
      return fetchedUser
    } catch {
      setUser(null)
      // A support tab never touches the user token slots.
      if (!isSupportSession()) clearTokens()
      return null
    }
  }

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // EP-160 section 2.3: a support tab signs in with the session token
      // only, even when localStorage holds a user refresh token, and never
      // refreshes. A 401 here leaves the tab on "เซสชันหมดอายุ".
      if (isSupportSession()) {
        if (getSupportSessionStatus() === 'active') await fetchUser()
        setIsLoading(false)
        return
      }
      if (hasRefreshToken()) {
        try {
          // Route the bootstrap refresh through the single-flight guard so React
          // StrictMode's double-invoked effect (dev) can't fire two concurrent
          // refreshes that race the rotate-on-use refresh token — the loser's
          // invalid_grant would otherwise clearTokens() and evaporate the session
          // on any protected deep-link / hard reload. Same guard the 401
          // interceptor uses; it reads the token, retries once, and setTokens().
          await refreshTokensSingleFlight()
          await fetchUser()
        } catch {
          clearTokens()
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string): Promise<User | null> => {
    const tokens: AuthTokens = await apiLogin(email, password, CLIENT_ID)
    setTokens(tokens)
    const loggedInUser = await fetchUser()
    // EP-77: register the operator device for FCM push (native-guarded, best-effort).
    // Never for a support tab (EP-160): the device is not the owner's.
    if (!isSupportSession()) void registerOperatorPush()
    return loggedInUser
  }

  const logout = async () => {
    // EP-77: unregister the device token while the access token is still valid.
    await unregisterOperatorPush()
    await apiLogout()
    // C1 — reset PostHog identity on logout so the next anonymous session
    // starts fresh (no lingering person association).
    resetIdentity()
    clearSentrySession()
    // WEB-H5: persisted Zustand stores survive logout — on a shared device the
    // next login inherited the previous user's selected property (silently
    // pre-filtering invoices/payments/parcels) and their recent searches.
    useSelectedPropertyStore.setState({ selectedPropertyId: null })
    useSelectedPropertyStore.persist.clearStorage()
    useSearchStore.getState().clearRecentSearches()
    useSearchStore.persist.clearStorage()
    setUser(null)
    queryClient.clear()
  }

  // T129-008: derived from user state, stays in sync with `user` automatically.
  const isAccountant = user !== null && user.role === 'accountant'

  // EP-150: see the comment on AuthContextType#clearSession. No network
  // calls, because the tokens this session held were already revoked
  // server-side by the account-deletion request that is about to call this.
  const clearSession = () => {
    resetIdentity()
    clearSentrySession()
    if (!isSupportSession()) clearTokens()
    queryClient.clear()
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAccountant,
        login,
        logout,
        clearSession,
      }}
    >
      {inRouter && <SessionReplayRoute />}
      {children}
    </AuthContext.Provider>
  )
}
